import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import { ChatStreamPendingError } from '@terra/shared-utils';
import { getSessionToken } from '../utils/getSessionToken';
import {
  asPlacement,
  openTurnPartKeysByMessageId,
  relocatePartsAfterAnchors,
  retargetPlacementAnchors,
  type UserMessagePlacement,
} from './copilot-user-message-order';

export type { UserMessagePlacement };

export const COPILOT_PENDING_FOLLOW_UP_HTTP_STATUS = 202;

const cachedCopilotPromoteResponses = new Map<string, Response>();

export function copilotPromoteResponseCacheKey({
  chatId,
  messageId,
}: {
  chatId: string;
  messageId: string;
}): string {
  return `${chatId}:${messageId}`;
}

export function readCopilotRequestMessageId(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') {
    return undefined;
  }

  const message = (body as { message?: { id?: unknown } }).message;

  return typeof message?.id === 'string' ? message.id : undefined;
}

export function readCopilotRequestMessageIdFromInit(
  options?: RequestInit,
): string | undefined {
  if (typeof options?.body !== 'string') {
    return undefined;
  }

  try {
    return readCopilotRequestMessageId(JSON.parse(options.body) as unknown);
  } catch {
    return undefined;
  }
}

export function toCopilotInvokeTransportResponse(response: Response): Response {
  if (response.status !== COPILOT_PENDING_FOLLOW_UP_HTTP_STATUS) {
    return response;
  }

  throw new ChatStreamPendingError();
}

export function getCopilotInvokeHeaders(
  chatId: string,
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Chat-ID': chatId,
  };
  const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

  if (!isElectron) {
    const sessionToken = getSessionToken();

    if (sessionToken) {
      headers.Authorization = `Bearer ${sessionToken}`;
    }
  }

  return headers;
}

export function cacheCopilotPromoteResponse({
  chatId,
  messageId,
  response,
}: {
  chatId: string;
  messageId: string;
  response: Response;
}): void {
  cachedCopilotPromoteResponses.set(
    copilotPromoteResponseCacheKey({ chatId, messageId }),
    response,
  );
}

export function takeCachedCopilotPromoteResponse({
  chatId,
  messageId,
}: {
  chatId: string;
  messageId: string;
}): Response | undefined {
  const cacheKey = copilotPromoteResponseCacheKey({ chatId, messageId });
  const response = cachedCopilotPromoteResponses.get(cacheKey);

  cachedCopilotPromoteResponses.delete(cacheKey);

  return response;
}

export function discardCachedCopilotPromoteResponse({
  chatId,
  messageId,
}: {
  chatId: string;
  messageId: string;
}): void {
  cachedCopilotPromoteResponses.delete(
    copilotPromoteResponseCacheKey({ chatId, messageId }),
  );
}

export function clearCachedCopilotPromoteResponses(): void {
  cachedCopilotPromoteResponses.clear();
}

export async function prepareStreamingCopilotFollowUpSend({
  url,
  chatId,
  body,
  fetchImpl = fetch,
}: {
  url: string;
  chatId: string;
  body: unknown;
  fetchImpl?: typeof fetch;
}): Promise<'pending' | 'ready-to-send' | 'rejected'> {
  const response = await fetchImpl(url, {
    method: 'POST',
    credentials: 'include',
    headers: getCopilotInvokeHeaders(chatId),
    body: JSON.stringify(body),
  });

  if (response.status === COPILOT_PENDING_FOLLOW_UP_HTTP_STATUS) {
    return 'pending';
  }

  if (!response.ok) {
    return 'rejected';
  }

  const messageId = readCopilotRequestMessageId(body);

  if (messageId) {
    cacheCopilotPromoteResponse({ chatId, messageId, response });
  }

  return 'ready-to-send';
}

export function placeUserMessageAfterPredecessors({
  messages,
  message,
  predecessorIds,
}: {
  messages: TerraUIMessage[];
  message: TerraUIMessage;
  predecessorIds: readonly string[];
}): TerraUIMessage[] {
  const current = messages.find((item) => item.id === message.id) ?? message;
  const messageIds = new Set(messages.map((item) => item.id));
  const missingPersistedPredecessor = predecessorIds.some(
    (id) => !isDisplayOnlyPlacementId(id) && !messageIds.has(id),
  );

  if (missingPersistedPredecessor) {
    return messages.some((item) => item.id === current.id)
      ? messages
      : [...messages, current];
  }

  const predecessorIdSet = new Set(predecessorIds);
  const firstPredecessorIndex = messages.findIndex((item) =>
    predecessorIdSet.has(item.id),
  );

  if (firstPredecessorIndex === -1) {
    return messages.some((item) => item.id === current.id)
      ? messages
      : [...messages, current];
  }

  const prefix = messages
    .slice(0, firstPredecessorIndex)
    .filter((item) => item.id !== current.id);
  const used = new Set(prefix.map((item) => item.id));
  const before: TerraUIMessage[] = [];

  for (const predecessorId of predecessorIds) {
    if (predecessorId === current.id || used.has(predecessorId)) {
      continue;
    }

    const predecessor = messages.find((item) => item.id === predecessorId);

    if (!predecessor) {
      continue;
    }

    before.push(predecessor);
    used.add(predecessorId);
  }

  const after = messages.filter(
    (item) => item.id !== current.id && !used.has(item.id),
  );

  return [...prefix, ...before, current, ...after];
}

export function applyUserMessagePlacements({
  messages,
  predecessorIdsByMessageId,
}: {
  messages: TerraUIMessage[];
  predecessorIdsByMessageId: ReadonlyMap<
    string,
    readonly string[] | UserMessagePlacement
  >;
}): TerraUIMessage[] {
  const placements = retargetPlacementAnchors({
    messages,
    placements: predecessorIdsByMessageId,
  });
  let placed = messages;

  for (const [messageId, placement] of placements) {
    const message = placed.find((item) => item.id === messageId);

    if (!message) {
      continue;
    }

    placed = placeUserMessageAfterPredecessors({
      messages: placed,
      message,
      predecessorIds: asPlacement(placement).predecessorIds,
    });
  }

  return relocatePartsAfterAnchors({
    messages: placed,
    placements,
  });
}

function dedupeChatMessages(messages: TerraUIMessage[]): TerraUIMessage[] {
  const seen = new Map<string, number>();
  const merged: TerraUIMessage[] = [];

  for (const message of messages) {
    const existingIndex = seen.get(message.id);

    if (existingIndex !== undefined) {
      if (message.role === 'assistant') {
        merged[existingIndex] = message;
      }

      continue;
    }

    seen.set(message.id, merged.length);
    merged.push(message);
  }

  return merged;
}

export function mergeMessagesWithBusyFollowUps({
  messages,
  followUps,
  predecessorIdsByMessageId,
}: {
  messages: TerraUIMessage[];
  followUps: TerraUIMessage[];
  predecessorIdsByMessageId?: ReadonlyMap<
    string,
    readonly string[] | UserMessagePlacement
  >;
}): TerraUIMessage[] {
  const withFollowUps = dedupeChatMessages(messages);
  const seen = new Set(withFollowUps.map((message) => message.id));

  for (const followUp of followUps) {
    if (seen.has(followUp.id)) {
      continue;
    }

    seen.add(followUp.id);
    withFollowUps.push(followUp);
  }

  if (!predecessorIdsByMessageId || predecessorIdsByMessageId.size === 0) {
    return withFollowUps;
  }

  return applyUserMessagePlacements({
    messages: withFollowUps,
    predecessorIdsByMessageId,
  });
}

const busyFollowUpsByChatId = new Map<string, Map<string, TerraUIMessage>>();

const userMessagePlacementsByChatId = new Map<
  string,
  Map<string, UserMessagePlacement>
>();

export const BACKGROUND_TURN_SHELL_ID_PREFIX = 'background-turn-shell:';
const AFTER_USER_SHELL_ID_PREFIX = 'after-user:';

export function isBackgroundTurnShellId(messageId: string): boolean {
  return messageId.startsWith(BACKGROUND_TURN_SHELL_ID_PREFIX);
}

function isDisplayOnlyPlacementId(messageId: string): boolean {
  return (
    messageId.startsWith(AFTER_USER_SHELL_ID_PREFIX) ||
    isBackgroundTurnShellId(messageId)
  );
}

export function rememberUserMessagePlacement({
  chatId,
  messageId,
  predecessorIds,
  predecessors,
  liveMessages,
}: {
  chatId: string;
  messageId: string;
  predecessorIds?: readonly string[];
  predecessors?: readonly TerraUIMessage[];
  liveMessages?: readonly TerraUIMessage[];
}): void {
  const placements =
    userMessagePlacementsByChatId.get(chatId) ??
    new Map<string, UserMessagePlacement>();
  const source = predecessors ?? [];
  const partKeysByMessageId = openTurnPartKeysByMessageId({
    predecessors: (liveMessages ?? source).filter(
      (message) => message.id !== messageId,
    ),
    followUpIds: new Set(placements.keys()),
  });

  placements.set(messageId, {
    predecessorIds:
      source.length > 0
        ? source.map((message) => message.id)
        : (predecessorIds ?? []),
    ...(Object.keys(partKeysByMessageId).length > 0
      ? { partKeysByMessageId }
      : {}),
  });
  userMessagePlacementsByChatId.set(chatId, placements);
}

export function userMessagePlacementForSend({
  predecessors,
  liveMessages,
}: {
  predecessors: readonly TerraUIMessage[];
  liveMessages: readonly TerraUIMessage[];
}): {
  predecessors: readonly TerraUIMessage[];
  liveMessages: readonly TerraUIMessage[];
} {
  return { predecessors, liveMessages };
}

export function forgetUserMessagePlacement({
  chatId,
  messageId,
}: {
  chatId: string;
  messageId: string;
}): void {
  const placements = userMessagePlacementsByChatId.get(chatId);

  if (!placements) {
    return;
  }

  placements.delete(messageId);

  if (placements.size === 0) {
    userMessagePlacementsByChatId.delete(chatId);
  }
}

export function readUserMessagePlacements(
  chatId: string,
): ReadonlyMap<string, UserMessagePlacement> {
  return userMessagePlacementsByChatId.get(chatId) ?? new Map();
}

export function clearUserMessagePlacements(chatId: string): void {
  userMessagePlacementsByChatId.delete(chatId);
}

export function rememberBusyCopilotFollowUp({
  chatId,
  message,
}: {
  chatId: string;
  message: TerraUIMessage;
}): void {
  const followUps =
    busyFollowUpsByChatId.get(chatId) ?? new Map<string, TerraUIMessage>();

  followUps.set(message.id, message);
  busyFollowUpsByChatId.set(chatId, followUps);
}

export function forgetBusyCopilotFollowUp({
  chatId,
  messageId,
}: {
  chatId: string;
  messageId: string;
}): void {
  const followUps = busyFollowUpsByChatId.get(chatId);

  if (!followUps) {
    return;
  }

  followUps.delete(messageId);

  if (followUps.size === 0) {
    busyFollowUpsByChatId.delete(chatId);
  }
}

export function forgetPersistedBusyCopilotFollowUps({
  chatId,
  persistedIds,
}: {
  chatId: string;
  persistedIds: ReadonlySet<string>;
}): void {
  const followUps = busyFollowUpsByChatId.get(chatId);

  if (!followUps) {
    return;
  }

  for (const messageId of followUps.keys()) {
    if (persistedIds.has(messageId)) {
      followUps.delete(messageId);
    }
  }

  if (followUps.size === 0) {
    busyFollowUpsByChatId.delete(chatId);
  }
}

export function readBusyCopilotFollowUps(chatId: string): {
  followUps: TerraUIMessage[];
} {
  const followUps = busyFollowUpsByChatId.get(chatId);

  if (!followUps) {
    return { followUps: [] };
  }

  return { followUps: [...followUps.values()] };
}

export function clearBusyCopilotFollowUps(chatId: string): void {
  busyFollowUpsByChatId.delete(chatId);
}

export function messagesForBackgroundTurn({
  messages,
  followUps,
  predecessorIdsByMessageId,
  shellId,
}: {
  messages: TerraUIMessage[];
  followUps: TerraUIMessage[];
  predecessorIdsByMessageId?: ReadonlyMap<
    string,
    readonly string[] | UserMessagePlacement
  >;
  shellId: string;
}): TerraUIMessage[] {
  const placed = mergeMessagesWithBusyFollowUps({
    messages,
    followUps,
    predecessorIdsByMessageId,
  });
  const last = placed.at(-1);

  if (
    last?.role === 'assistant' &&
    isBackgroundTurnShellId(last.id) &&
    last.parts.length === 0
  ) {
    return placed;
  }

  return [
    ...placed,
    {
      id: shellId,
      role: 'assistant',
      parts: [],
    },
  ];
}
